import {
  MAX_ATTRIBUTION_VALUE_LENGTH,
  type MarketingAttribution,
  marketingAttributionSchema,
} from "../../shared/mutators/completeProfile";

// login.tines.com holds the marketing-attribution cookies (set on .tines.com
// by the marketing site), which never reach 3B's domain — so it forwards them
// as a query param on the post-provision redirect to the new tenant. We
// capture the param to localStorage at boot, before the SSO redirect discards
// the URL, and attach it to the completeProfile mutation for the HQ lead
// webhook.
export const SIGNUP_ATTRIBUTION_PARAM = "signupAttribution";
const STORAGE_KEY = "3b-signup-attribution";

const ATTRIBUTION_KEYS = Object.keys(
  marketingAttributionSchema.shape
) as (keyof MarketingAttribution)[];

// Storage-blocked browsers throw on any localStorage access; attribution is
// best-effort, so storage failures are swallowed — capture runs at module
// init, where an uncaught throw would break app boot entirely.
export function captureSignupAttribution(): void {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(SIGNUP_ATTRIBUTION_PARAM);
  if (raw === null) return;
  url.searchParams.delete(SIGNUP_ATTRIBUTION_PARAM);
  history.replaceState(history.state, "", url.toString());
  const attribution = parseAttribution(raw);
  if (!attribution) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {}
}

export function readSignupAttribution(): MarketingAttribution | undefined {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  return parseAttribution(raw);
}

export function clearSignupAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

function parseAttribution(raw: string): MarketingAttribution | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) return undefined;
  const record = parsed as Record<string, unknown>;
  // Login historically emitted "GCLID"; accept it as an alias for gclid so
  // in-flight redirects during rollout still land.
  if (typeof record.gclid !== "string" && typeof record.GCLID === "string") {
    record.gclid = record.GCLID;
  }
  const attribution: MarketingAttribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = record[key];
    if (typeof value === "string" && value.length > 0) {
      attribution[key] = value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
    }
  }
  return Object.keys(attribution).length > 0 ? attribution : undefined;
}
